import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Theme, TextField, Button, Typography, Box, Paper } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, NavLink } from 'react-router-dom';
import { auth } from '../../services/firebase.config';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    backgroundColor: 'white',
    boxShadow: '0 8px 32px rgba(44, 62, 80, 0.15)',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  logo: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: '#6c757d',
    fontSize: '1rem',
  },
  form: {
    width: '100%',
  },
  textFieldContainer: {
    marginBottom: theme.spacing(3),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#3498db',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2980b9',
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#2c3e50',
      '&.Mui-focused': {
        color: '#2980b9',
      },
    },
  },
  loginButton: {
    backgroundColor: '#2980b9',
    color: 'white',
    padding: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '&:hover': {
      backgroundColor: '#3498db',
    },
  },
  signupLink: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
  },
  link: {
    color: '#2980b9',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  backLink: {
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(3),
    color: 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      color: 'white',
    },
  },
  errorAlert: {
    marginBottom: theme.spacing(2),
  },
}));

const Login = () => {
  const history = useHistory();
  const classes = useStyles();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    signInWithEmailAndPassword(auth, email, password)
      .then(userCredential => {
        // Signed in
        const user = userCredential.user;
        history.replace('/dashboard');
        console.log(user);
      })
      .catch((authError: any) => {
        const errorCode = authError.code;
        const errorMessage = authError.message;
        setError('Invalid email or password. Please try again.');
        console.log(errorCode, errorMessage);
      });
  };

  return (
    <div className={classes.root}>
      <NavLink to="/" className={classes.backLink}>
        ← Back to Home
      </NavLink>

      <Paper className={classes.paper} elevation={0}>
        <Box className={classes.header}>
          <Typography variant="h4" className={classes.logo}>
            BLUESTREAM
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Sign in to your account
          </Typography>
        </Box>

        <form onSubmit={onLogin} className={classes.form}>
          {error && (
            <Alert severity="error" className={classes.errorAlert}>
              {error}
            </Alert>
          )}

          <div className={classes.textFieldContainer}>
            <TextField
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              label="Email Address"
              placeholder="Enter your email"
              variant="outlined"
              fullWidth
              className={classes.textField}
            />
          </div>

          <div className={classes.textFieldContainer}>
            <TextField
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              label="Password"
              placeholder="Enter your password"
              variant="outlined"
              fullWidth
              className={classes.textField}
            />
          </div>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={classes.loginButton}
            disabled={!email || !password}
          >
            Sign In
          </Button>
        </form>

        <div className={classes.signupLink}>
          <Typography variant="body2" style={{ color: '#6c757d' }}>
            Don't have an account?{' '}
            <NavLink to="/signup" className={classes.link}>
              Sign up here
            </NavLink>
          </Typography>
        </div>
      </Paper>
    </div>
  );
};

export default Login;
